class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.searchIndexStorageKey = 'dkgProductSearchIndexV2';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    if (this.searchTerm.length < 2) {
      this.close();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (currentSearchTerm.length < 2) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onClick(event) {
    const tab = event.target.closest('[data-dkg-search-tab]');
    if (!tab || !this.contains(tab)) return;

    event.preventDefault();
    const panel = tab.dataset.dkgSearchTab;

    this.querySelectorAll('[data-dkg-search-tab]').forEach((item) => {
      const isActive = item.dataset.dkgSearchTab === panel;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.querySelectorAll('[data-dkg-search-panel]').forEach((item) => {
      item.classList.toggle('is-active', item.dataset.dkgSearchPanel === panel);
    });
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    if (this.classList.contains('dkg-main-predictive-search')) {
      this.getMainSearchResults(searchTerm, queryKey);
      return;
    }

    this.fetchShopifySearchResults(searchTerm, queryKey);
  }

  fetchShopifySearchResults(searchTerm, queryKey) {
    const searchParams = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
    });

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  async getMainSearchResults(searchTerm, queryKey) {
    try {
      const searchIndex = await this.getSearchIndex();
      const products = this.getProductResults(searchTerm, searchIndex.products);
      const resultsMarkup = this.buildMainSearchMarkup(products, searchTerm);

      this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
        if (predictiveSearchInstance.classList.contains('dkg-main-predictive-search')) {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        }
      });

      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      this.fetchShopifySearchResults(searchTerm, queryKey);
    }
  }

  async getSearchIndex() {
    if (window.dkgSearchIndex) return window.dkgSearchIndex;

    const cachedIndex = this.readSearchIndexCache();
    if (cachedIndex) {
      window.dkgSearchIndex = cachedIndex;
      return window.dkgSearchIndex;
    }

    const response = await fetch(`${routes.root_url}?section_id=dkg-search-index`);
    if (!response.ok) throw new Error('Search index failed');

    const text = await response.text();
    const documentFragment = new DOMParser().parseFromString(text, 'text/html');
    const productsScript = documentFragment.querySelector('[data-dkg-product-search-data]');

    const products = productsScript ? JSON.parse(productsScript.textContent || '[]') : [];

    window.dkgSearchIndex = { products };
    this.writeSearchIndexCache(window.dkgSearchIndex);

    return window.dkgSearchIndex;
  }

  readSearchIndexCache() {
    try {
      const cached = window.sessionStorage.getItem(this.searchIndexStorageKey);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      if (!Array.isArray(parsed.products)) return null;

      return parsed;
    } catch (error) {
      return null;
    }
  }

  writeSearchIndexCache(searchIndex) {
    try {
      window.sessionStorage.setItem(this.searchIndexStorageKey, JSON.stringify(searchIndex));
    } catch (error) {
      // Storage can fail in private browsing or when the quota is full.
    }
  }

  getProductResults(searchTerm, source = []) {
    const query = this.normalizeSearchText(searchTerm);
    if (!query) return [];

    return source
      .map((product, index) => {
        const title = this.normalizeSearchText(product.title || '');
        const terms = this.normalizeSearchText(product.terms || '');
        let score = 0;

        if (title === query) {
          score = 100;
        } else if (title.startsWith(query)) {
          score = 90;
        } else if (title.includes(query)) {
          score = 80;
        } else if (terms.includes(query)) {
          score = 40;
        }

        return { ...product, _score: score, _index: index };
      })
      .filter((product) => product._score > 0)
      .sort((a, b) => b._score - a._score || a._index - b._index)
      .slice(0, 4);
  }

  normalizeSearchText(value) {
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/\s+/g, ' ')
      .trim();
  }

  buildMainSearchMarkup(products, searchTerm) {
    const safeTerm = this.escapeHTML(searchTerm);
    const visibleProducts = products.slice(0, 3);
    const hasMoreProducts = products.length > visibleProducts.length;
    const productItems = visibleProducts
      .map((product, index) => {
        const title = this.escapeHTML(product.title || '');
        const url = this.escapeAttribute(product.url || '#');
        const image = this.getProductImage(product);

        return `
          <li
            id="predictive-search-option-product-${index + 1}"
            class="predictive-search__list-item"
            role="option"
            aria-selected="false"
          >
            <a
              href="${url}"
              class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text"
              tabindex="-1"
            >
              ${
                image
                  ? `<img class="predictive-search__image" src="${this.escapeAttribute(image)}" alt="${title}" width="52" height="52" loading="lazy" decoding="async" fetchpriority="low">`
                  : '<span class="predictive-search__image predictive-search__image--empty" aria-hidden="true"></span>'
              }
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading h5">${title}</p>
              </div>
            </a>
          </li>
        `;
      })
      .join('');
    const totalResults = products.length;
    const resultsText =
      totalResults === 1 ? '1 Ergebnis gefunden' : `${totalResults} Ergebnisse gefunden`;

    if (!totalResults) {
      return `
        <div id="predictive-search-results" role="listbox">
          <div class="predictive-search__empty">Keine direkten Treffer für "${safeTerm}"</div>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value">Keine Treffer</span>
      `;
    }

    return `
      <div id="predictive-search-results" role="listbox">
        <div class="dkg-predictive-tabs" role="tablist" aria-label="Suchvorschlaege">
          <button type="button" class="dkg-predictive-tab is-active" role="tab" aria-selected="true" data-dkg-search-tab="products">Produkte</button>
        </div>
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
          <div class="predictive-search__result-group dkg-predictive-panel is-active" data-dkg-search-panel="products">
                  <div>
                    <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-label="Produkte">
                      ${productItems}
                    </ul>
                    ${
                      hasMoreProducts
                        ? `<button class="predictive-search__item dkg-predictive-more-button link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
                            <span>Alle Ergebnisse anzeigen</span>
                            <span class="svg-wrapper">
                              <svg class="icon icon-arrow" aria-hidden="true" focusable="false" viewBox="0 0 14 10">
                                <path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"></path>
                              </svg>
                            </span>
                          </button>`
                        : ''
                    }
                  </div>
                </div>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${resultsText}</span>
    `;
  }

  getProductImage(product) {
    const image = product.featured_image?.url || product.image || product.featured_image;
    if (!image || typeof image !== 'string') return '';
    return image;
  }

  escapeHTML(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeAttribute(value) {
    return this.escapeHTML(value);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
